import { useState } from "react"
import { Link } from "expo-router"
import { SafeAreaView } from "react-native-safe-area-context"
import { Pressable, View, ActivityIndicator } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"

import { Header } from "components/Header"
import { ar } from "i18n/ar"
import { trpc } from "utils/trpc"
import { formatDateForDisplay, localDateToUnix, unixToLocalDate, dayjs } from "utils/dayjs"
import { Text, ModalWrapper, Button } from "components/core"
import { List } from "@src/components/list"
import { DatePicker } from "components/date-picker"
import { useNumCols } from "@src/hooks"
import { priceFromDBFormat, sharedStyles } from "@src/utils"

const CASHIERS = {
  "019af0cf-46f6-7694-b68c-e586aeb513a3": " - 1",
  "019aea27-e144-76cc-9e2c-5dd973f7ec05": " - 2",
}

export default function Shifts() {
  const [filterVisible, setFilterVisible] = useState(false)
  const [fromDate, setFromDate] = useState<number>(() => dayjs().subtract(7, "day").startOf("day").unix())
  const [toDate, setToDate] = useState<number>(() => dayjs().endOf("day").unix())
  const [localFrom, setLocalFrom] = useState<number | undefined>(undefined)
  const [localTo, setLocalTo] = useState<number | undefined>(undefined)

  const _shifts = trpc.shifts.list.useInfiniteQuery({ limit: 20, from: fromDate, to: toDate }, { getNextPageParam: (lastPage) => lastPage.nextCursor })
  const shifts = _shifts.data?.pages.flatMap((page) => page.items) ?? []
  const employees = trpc.employees.list.useQuery()
  const numCols = useNumCols(380, { maxWidth: 1400 })

  const applyFilters = () => {
    setFromDate(localFrom!)
    setToDate(localTo!)
    setFilterVisible(false)
  }

  const openFilter = () => {
    setLocalFrom(fromDate)
    setLocalTo(toDate)
    setFilterVisible(true)
  }

  return (
    <>
      <Header
        title={ar.shifts}
        right={
          <Pressable onPress={openFilter} className="px-2 py-1 active:opacity-70 h-full justify-center items-center">
            <MIcon name="filter-outline" size={26} color="#374151" />
          </Pressable>
        }
      />

      {filterVisible && (
        <ModalWrapper onClose={() => setFilterVisible(false)}>
          <View className="gap-4">
            <Text className="font-bold text-lg text-center mb-2">{ar.applyFilters}</Text>

            <View>
              <Text className="mb-2 font-medium">{ar.dateRange}</Text>
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.fromDate}</Text>
                  <DatePicker
                    mode="datetime"
                    value={localFrom ? unixToLocalDate(localFrom) : undefined}
                    onChange={(d) => setLocalFrom(d ? localDateToUnix(d) : undefined)}
                    placeholder={ar.fromDate}
                  />
                </View>
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.toDate}</Text>
                  <DatePicker
                    mode="datetime"
                    value={localTo ? unixToLocalDate(localTo) : undefined}
                    onChange={(d) => setLocalTo(d ? localDateToUnix(d) : undefined)}
                    placeholder={ar.toDate}
                  />
                </View>
              </View>
            </View>

            <Button onPress={applyFilters}>{ar.apply}</Button>
          </View>
        </ModalWrapper>
      )}

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={shifts}
        numColumns={numCols}
        key={`shifts-grid-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        onRefresh={_shifts.refetch}
        estimatedItemSize={160}
        ListEmptyComponent={
          _shifts.isLoading || employees.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">{ar.noShifts}</Text>
        }
        renderItem={({ item }) => {
          const employee = employees.data?.find((employee) => employee.id === item.employeeId)
          const isOpen = item.status === "open"
          const cash = isOpen ? (item._cash ?? 0) : item.cash
          const visa = isOpen ? (item._visa ?? 0) : item.visa
          const wallet = isOpen ? (item._wallet ?? 0) : item.wallet
          const total = cash + visa + wallet

          const statusConfig = {
            open: { bg: "bg-green-100", text: "text-green-700", label: ar.inProgress },
            pending: { bg: "bg-amber-100", text: "text-amber-700", label: ar.pendingConfirmation },
            confirmed: { bg: "bg-gray-100", text: "text-gray-700", label: ar.confirmed },
          }[item.status as "open" | "pending" | "confirmed"] ?? { bg: "bg-gray-100", text: "text-gray-700", label: ar.ended }

          return (
            <Link href={`/dashboard/shifts/${item.id}`} asChild>
              <Pressable className="flex-1 mx-1 mb-2 bg-white rounded overflow-hidden shadow-sm">
                <View className="p-4 border-b border-gray-100">
                  <View className="flex-row justify-between items-start mb-2">
                    <Text className="font-bold text-lg text-left">
                      #{item.id} {employee?.name} {CASHIERS[item.deviceId as keyof typeof CASHIERS] ?? "🚨"}
                    </Text>
                    <View className={`px-2 py-1 rounded-full ${statusConfig.bg}`}>
                      <Text className={`text-xs font-semibold ${statusConfig.text}`}>{statusConfig.label}</Text>
                    </View>
                  </View>
                  <Text className="text-gray-600 text-xs text-left">
                    {item.startTime ? formatDateForDisplay(item.startTime) : "—"}
                    {" ←← "}
                    {item.endTime ? formatDateForDisplay(item.endTime) : "—"}
                  </Text>
                </View>

                <View className="p-4">
                  <View className="flex-row justify-between">
                    <View className="items-center">
                      <Text className="text-gray-600 text-xs">{ar.cash}</Text>
                      <Text className="font-bold">{priceFromDBFormat(cash)}</Text>
                    </View>
                    <View className="items-center">
                      <Text className="text-gray-600 text-xs">{ar.visa}</Text>
                      <Text className="font-bold">{priceFromDBFormat(visa)}</Text>
                    </View>
                    <View className="items-center">
                      <Text className="text-gray-600 text-xs">{ar.wallet}</Text>
                      <Text className="font-bold">{priceFromDBFormat(wallet)}</Text>
                    </View>
                    <View className="items-center">
                      <Text className="text-gray-600 text-xs">{ar.total}</Text>
                      <Text className="font-bold text-blue-600">{priceFromDBFormat(total)}</Text>
                    </View>
                  </View>
                </View>
              </Pressable>
            </Link>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        onEndReachedThreshold={0.8}
        onEndReached={() => {
          _shifts.hasNextPage && _shifts.fetchNextPage()
        }}
      />
    </>
  )
}
